import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2 } from "lucide-react";
import api from "@/lib/api";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients?search=${encodeURIComponent(q)}`);
        setResults(data.items.slice(0, 8));
        setOpen(true);
      } catch (e) {
        setResults([]);
        setOpen(true);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (id) => { setOpen(false); setQ(""); navigate(`/clients/${id}`); };

  return (
    <div className="relative w-full max-w-xl" ref={boxRef}>
      <div className="flex items-center gap-2 h-10 px-3 rounded-sm border border-border bg-secondary/60 focus-within:ring-2 focus-within:ring-primary transition-shadow">
        <Search size={17} className="text-muted-foreground shrink-0" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder="Search anyone by name, phone or email…"
          className="w-full bg-transparent outline-none text-sm placeholder:text-muted-foreground"
        />
      </div>
      {open && (
        <div className="absolute mt-2 w-full rounded-sm border border-border bg-popover shadow-lg overflow-hidden z-50" data-testid="search-results">
          {loading && <div className="px-4 py-3 text-sm text-muted-foreground">Searching…</div>}
          {!loading && results.length === 0 && <div className="px-4 py-3 text-sm text-muted-foreground">No clients found.</div>}
          {results.map((c) => (
            <button key={c.id} onClick={() => go(c.id)} data-testid={`search-result-${c.id}`}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent transition-colors border-b border-border last:border-0">
              <div className="h-8 w-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><Building2 size={15} /></div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground font-mono truncate">{c.phone || "—"} · {c.email || "—"}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
